import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { toast } from 'react-toastify'
import PortAlertService, { CountryUnLocode, PortAlert, PortAlerts, PortAlertWidgets } from './portAlertsService'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CountryUNLocodeList } from '../helpers/countryUnLocode'
import { useGetUserOrganizationsQuery } from 'user/userService'
import { Organization } from 'user/userSlice'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { VesselStay } from './useFetchVesselStays'
import { format, parse } from 'date-fns';

export const usePortAlerts = ({ org_ids }: { org_ids: string[] | undefined }) => {
  const queryClient = useQueryClient()
  const portAlertService = PortAlertService()

  const { data: organizations, error: organizationError, isLoading: organizationIsLoading, } = useGetUserOrganizationsQuery()
  const chosenOrganization = useSelector(
    (state: RootState) => state.user.chosenOrganization
  )

  const useCustomQuery = () => {
    return useQuery(['portAlerts', org_ids], () => portAlertService.getPortAlertsByOrgIDs(org_ids), {})
  }
  const { data: portAlerts, isLoading } = useCustomQuery()
 
  // Mutations for create, update, and delete
  // Create
  const createMutation = useMutation(
    (variables: { newPortAlert: PortAlert }) =>
      portAlertService.createPortAlert(variables.newPortAlert),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['portAlerts'])
      },
      onError: (error: Error) => {
        console.error("Error during createPortAlert:", error.message)
      },
    })

  const handleCreate = async (newPortAlert: PortAlert): Promise<PortAlert> => {
    try {
      const createdPortAlert = await createMutation.mutateAsync({
        newPortAlert,
      })
      return createdPortAlert as PortAlert // Explicitly cast to PortAlert
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data
        const errorMessage = responseData || 'Unknown error occurred'
        toast.warn(`Unable to create portAlert - ${errorMessage}`, {
          position: "bottom-right",
          autoClose: 8000,
        })
      }
      throw error
    }
  }

  // Update
  const updateMutation = useMutation(
    (variables: { updatedPortAlert: PortAlert }) => portAlertService.updatePortAlert(variables.updatedPortAlert), {
    onSuccess: () => {
      queryClient.invalidateQueries(['portAlerts'])
    },
  })

  const handleUpdate = async (updatedPortAlert: PortAlert): Promise<PortAlert> => {
    try {
      const result = await updateMutation.mutateAsync({ updatedPortAlert })
      return result as PortAlert
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data
        const errorMessage = responseData || 'Unknown error occurred'
        toast.warn(`Unable to udpate portAlert - ${errorMessage}`, {
          position: "bottom-right",
          autoClose: 8000,
        })
      }

      throw error
    }
  }

  // Delete
  const deleteMutation = useMutation(portAlertService.deletePortAlert, {
    onSuccess: () => {
      queryClient.invalidateQueries(['portAlerts'])
    },
  })

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data
        const errorMessage = responseData || 'Unknown error occurred'
        toast.warn(`Unable to delete portAlert - ${errorMessage}`, {
          position: "bottom-right",
          autoClose: 8000,
        })
      }

      throw error
    }
  }

  const transformPortAlerts = (alerts: PortAlerts | null,
    countries: CountryUnLocode[] | null,
    organizations: Organization[] | null): PortAlert[] | null => {

    if (!alerts || !countries || !organizations) return null

    // Create lookup maps for quick cross-referencing
    const countryMap = new Map(countries.map((country) => [country.value, country.label]))
    const organizationMap = new Map(organizations.map((org) => [org.id, org.display_name]))

    // Transform each alert
    const transformedAlerts = alerts.map((alert) => ({
      id: alert.id,
      organizationId: alert.organizationId,
      organization_name: organizationMap.get(alert.organizationId) || 'Unknown Organization',
      countryUnlocode: alert.countryUnlocode,
      country_name: countryMap.get(alert.countryUnlocode) || 'Unknown Country',
      description: alert.description,
      createdBy: alert.createdBy,
      createdAt: alert.createdAt,
      updatedBy: alert.updatedBy,
      updatedAt: alert.updatedAt,
    }))

    // Sort by organization_name (ascending) then country_name (ascending)
    return transformedAlerts.sort((a, b) => {
      if (a.organization_name !== b.organization_name) {
        return a.organization_name.localeCompare(b.organization_name)
      }
      return a.country_name.localeCompare(b.country_name)
    })
  }

  const filterAndFormatVesselStays = (
    vesselStays: VesselStay[],
    portAlerts: PortAlerts
  ): PortAlertWidgets => {
    // get all country_unlocodes from portAlerts
    const country_unlocodes = portAlerts?.filter(pa => pa.organizationId === chosenOrganization?.id).map(pa => pa.countryUnlocode)
    if (country_unlocodes && vesselStays) {
      const filteredByCountryLocode = vesselStays
        .filter((stay) =>
          country_unlocodes.some((code) => (stay.port_locode + "").startsWith(code))
        )
      return filteredByCountryLocode
        .sort((a, b) => {
          const dateA = parse(a.date_from, 'yyyy-MM-dd', new Date());
          const dateB = parse(b.date_from, 'yyyy-MM-dd', new Date());
          return dateB.getTime() - dateA.getTime(); // Descending order
        })
        .map((stay) => ({
          port: stay.port,
          date_from: format(parse(stay.date_from, 'yyyy-MM-dd', new Date()), "MMM ''yy"),
        }))
    }
    return null
  }
  
  return {
    countriesUNLocode: CountryUNLocodeList,
    organizations,
    organizationError,
    organizationIsLoading,
    chosenOrganization,
    portAlerts: transformPortAlerts(portAlerts ?? null, CountryUNLocodeList ?? null, organizations ?? null),
    portAlertsVesselStays: useCallback((vesselStays: VesselStay[], portAlerts: PortAlerts) => filterAndFormatVesselStays(vesselStays, portAlerts), [portAlerts]),
    isLoading,
    createPortAlert: handleCreate,
    updatePortAlert: handleUpdate,
    deletePortAlert: handleDelete
  }
}
